import { tradeRequestApi } from 'apiConnector/quote/trade';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import type { DocType, PriceAdjustmentUserOverride } from 'epic/common-form/Trade';
import { thunksByWorkspaceAndDoc } from 'epic/streamingQuote/utils';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { BackendError } from 'models/NextWorkflowError';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { EMPTY, merge, of } from 'rxjs';
import { catchError, filter, mergeMap, startWith, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { commonStreamActions } from 'state/forms/actions/stream';
import { commonTradeAction } from 'state/forms/actions/trade';
import type { PriceEnhancement, PriceEnhancementMode, TradeExecReport } from 'state/quotes/trades/trades.model';
import { getStaticFormQuoteId } from 'state/selectors';
import type { Dispatchable, EpicWithThunk } from 'state/state.common';
import { throwOnTimeout, wrapError } from 'utils/rxjs';
import { fatEventApiConnector } from 'utils/streaming/api';
import { tradeExecutionReport } from 'utils/streaming/messages';

function handleTradeReport(
  tabId: string,
  quoteId: string,
  { status, errors, bookingSkipped }: TradeExecReport,
  when: Date,
): Observable<Dispatchable> {
  switch (status) {
    case 'FULLY_EXECUTED':
      return of(commonTradeAction.quoteTradeDone('static', tabId, quoteId, 'FULLY_EXECUTED', when, bookingSkipped));
    case 'CANCELLED':
    case 'EXPIRED':
    case 'FAILED':
    case 'REJECTED':
      return of(commonTradeAction.quoteTradeRejected('static', tabId, quoteId, errors!, when));
    default:
      return EMPTY;
  }
}

export function requestStreamingTradeEpic(): EpicWithThunk {
  return (action$, state$, { oldHttpClient, selectors: { getTradeRequest }, getNow, getSgmeConfiguration }) =>
    action$.pipe(
      ofType('STREAMING_TRADE_REQUESTED'),
      filter(a => a.workspace === 'static'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const quoteId = getStaticFormQuoteId(state, tabId)!;
        const tradeRequest = getTradeRequest(state, tabId);
        const tradeAckTimeoutMs = getSgmeConfiguration().tradeAckTimeoutMs;
        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const tradeResponse$ = tradeRequestApi(oldHttpClient, tradeAckTimeoutMs).requestTrade(tradeRequest);
        return tradeResponse$.pipe(
          takeUntil(tabRemoved$),
          mergeMap(response => {
            const documentThunks = Object.entries(response.documentation).map(([docType, documents]) =>
              of(
                thunksByWorkspaceAndDoc.static[docType as DocType]?.(
                  tabId,
                  quoteId,
                  {
                    quoteId,
                    requestId: quoteId,
                    listResources: documents.map(({ url, ...others }) => ({ uri: url, ...others })),
                  },
                  true,
                ),
              ),
            );

            return merge(
              ...documentThunks,
              of(commonStreamActions.quoteStreamingTradeDone('static', tabId, getNow(), response)),
            );
          }),
          catchError(error => {
            return handleError(tabId, quoteId, error, getNow());
          }),
        );
      }),
    );
}
const handleError = (tabId: string, quoteId: string, error: Error, when: Date) => {
  if (apiErrorHelper.isHttpError(error) && error.status === 400) {
    const errors = error.response as BackendError;
    return of(commonTradeAction.quoteTradeRejected('static', tabId, quoteId, errors.rejections, when));
  }
  return of(
    commonTradeAction.quoteTradeFailed(
      'static',
      tabId,
      quoteId,
      apiErrorHelper.toApiErrorMessage(error, {
        timeoutErrorMessage: 'trade.errorMessages.requestTimeout',
      }),
      error,
    ),
  );
};

export function mapPriceAdjustmentUserOverride(
  mode: PriceEnhancementMode,
  enhancement: PriceEnhancement | undefined,
): PriceAdjustmentUserOverride | null {
  if (mode === 'None' || enhancement === undefined) {
    return null;
  }
  const { tradingOffer, xMargin: salesMargin } = enhancement;
  return { tradingOffer, salesMargin };
}

export function requestTradeEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (
    action$,
    state$,
    { oldHttpClient, selectors: { getTradeRequest, getStaticFormQuoteId }, getSgmeConfiguration, streaming, getNow },
  ) =>
    action$.pipe(
      ofType('QUOTE_TRADE_REQUESTED'),
      filter(a => a.workspace === 'static'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const quoteId = getStaticFormQuoteId(state, tabId)!;
        const tradeRequest = getTradeRequest(state, tabId);

        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const tradeAckTimeoutMs = getSgmeConfiguration().tradeAckTimeoutMs;
        const tradeExecReportTimeoutMs = getSgmeConfiguration().tradeExecReportTimeoutMs;
        return tradeRequestApi(oldHttpClient, tradeAckTimeoutMs)
          .requestTrade(tradeRequest)
          .pipe(
            takeUntil(tabRemoved$),
            mergeMap(() => {
              const tradeFailed$ = action$.pipe(
                ofType('QUOTE_TRADE_FAILED'),
                filter(a => a.workspace === 'static' && a.tabId === tabId),
              );
              const completionAction$ = tradeExecutionReport(streaming).pipe(
                takeUntil(tradeFailed$),
                filter(r => r.quoteId === quoteId),
                mergeMap(lightEvent =>
                  fatEventApiConnector(oldHttpClient)
                    .getFatEvent<TradeExecReport>(lightEvent)
                    .pipe(
                      mergeMap(report => handleTradeReport(tabId, quoteId, report, getNow())),
                      wrapError(
                        error =>
                          new apiErrorHelper.EpicApiError('trade.errorMessages.executionReportTechnicalError', error),
                        appScheduler,
                      ),
                    ),
                ),
                takeUntil(tabRemoved$),
                take(1),
                throwOnTimeout(
                  tradeExecReportTimeoutMs,
                  new apiErrorHelper.TimeoutError(
                    tradeExecReportTimeoutMs,
                    'trade.errorMessages.executionReportTimeout',
                  ),
                  appScheduler,
                ),
                startWith(commonTradeAction.quoteTradeSent('static', tabId, quoteId)),
              );

              return completionAction$;
            }),
            catchError(error => {
              if (apiErrorHelper.isHttpError(error) && error.status === 400) {
                return of(commonTradeAction.quoteTradeRejected('static', tabId, quoteId, error.response, getNow()));
              }

              return of(
                commonTradeAction.quoteTradeFailed(
                  'static',
                  tabId,
                  quoteId,
                  apiErrorHelper.toApiErrorMessage(error, {
                    timeoutErrorMessage: 'trade.errorMessages.requestTimeout',
                  }),
                  error,
                ),
              );
            }),
          );
      }),
    );
}
